import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, MessageCircle, Mail, Download, Loader2, Plus, Send, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken, formatApiError } from "../../lib/api";
import { colorFor, FOLLOWUP_STATUSES } from "../../lib/constants";
import { ScoreMeter } from "../../components/ScoreMeter";
import { AdminShell } from "./AdminShell";
import { useBrand } from "../../context/BrandContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
      <div className="font-heading font-bold text-navy mb-4">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-navy mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const s = useBrand();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [fuDate, setFuDate] = useState("");
  const [newTag, setNewTag] = useState("");

  const load = () => api.get(`/admin/leads/${leadId}`).then((res) => setData(res.data)).catch(() => toast.error("Could not load lead."));
  useEffect(() => { load(); }, [leadId]);

  if (!data) return <AdminShell title="Lead"><div className="grid place-items-center py-20 text-navy"><Loader2 className="animate-spin" /></div></AdminShell>;

  const { lead, report, answers_detail } = data;
  const q = lead.qualification || {};

  const updateStatus = async (v) => {
    await api.patch(`/admin/leads/${leadId}`, { follow_up_status: v });
    setData((d) => ({ ...d, lead: { ...d.lead, follow_up_status: v } }));
    toast.success("Status updated.");
  };
  const setFollowUp = async () => {
    if (!fuDate) return;
    await api.patch(`/admin/leads/${leadId}`, { follow_up_date: fuDate });
    toast.success("Follow-up date added.");
    setFuDate("");
    load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/leads/${leadId}/notes`, { text: note.trim() });
      setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };
  const saveTags = async (tags) => {
    setData((d) => ({ ...d, lead: { ...d.lead, tags } }));
    try {
      await api.put(`/admin/leads/${leadId}/tags`, { tags });
    } catch (e) { toast.error("Could not update tags."); load(); }
  };
  const addTag = () => {
    const t = newTag.trim();
    if (!t) return;
    const cur = data.lead.tags || [];
    if (cur.some((x) => x.toLowerCase() === t.toLowerCase())) { setNewTag(""); return; }
    saveTags([...cur, t]);
    setNewTag("");
  };
  const removeTag = (t) => saveTags((data.lead.tags || []).filter((x) => x !== t));
  const downloadPdf = () => {
    fetch(`${API_BASE}/admin/leads/${leadId}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b);
        a.download = `report-${lead.full_name}.pdf`; a.click();
      }).catch(() => toast.error("Download failed."));
  };
  const whatsapp = () => {
    const num = lead.mobile.replace(/\D/g, "");
    const tmpl = (s.whatsapp_template || "Hi {name}, your score is {score}/60.")
      .replace("{name}", lead.full_name).replace("{score}", lead.score)
      .replace("{priority1}", lead.priorities?.[0]?.title || "")
      .replace("{priority2}", lead.priorities?.[1]?.title || "")
      .replace("{priority3}", lead.priorities?.[2]?.title || "")
      .replace("{advisor}", s.advisor_name || "").replace("{business}", s.business_name || "");
    window.open(`https://wa.me/91${num}?text=${encodeURIComponent(tmpl)}`, "_blank");
  };

  const PRIORITY_COLOR = { HOT: "#D64545", WARM: "#F2A900", NURTURE: "#123D73" };

  return (
    <AdminShell title="Lead Detail" actions={
      <button onClick={() => navigate("/admin")} className="text-sm text-slate-500 flex items-center gap-1.5 hover:text-navy transition-colors duration-200">
        <ArrowLeft size={16} /> Back
      </button>
    }>
      {/* header */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-navy text-gold font-heading font-extrabold text-xl">
            {lead.full_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-heading font-extrabold text-navy text-xl">{lead.full_name}</div>
            <div className="text-sm text-slate-400">{lead.email} · {lead.mobile}</div>
          </div>
          <span className="ml-2 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide"
            style={{ background: `${PRIORITY_COLOR[lead.lead_priority]}1a`, color: PRIORITY_COLOR[lead.lead_priority] }}>
            {lead.lead_priority} LEAD
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="action-call" href={`tel:${lead.mobile}`} className="rounded-full bg-navy text-white text-sm font-semibold px-4 py-2 flex items-center gap-1.5 hover:bg-royal transition-colors duration-300"><Phone size={15} /> Call</a>
          <button data-testid="action-whatsapp" onClick={whatsapp} className="rounded-full bg-brandgreen text-white text-sm font-semibold px-4 py-2 flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform duration-300"><MessageCircle size={15} /> WhatsApp</button>
          <a data-testid="action-email" href={`mailto:${lead.email}`} className="rounded-full border border-slate-200 text-navy text-sm font-semibold px-4 py-2 flex items-center gap-1.5 hover:bg-slate-50 transition-colors duration-200"><Mail size={15} /> Email</a>
          <button data-testid="action-pdf" onClick={downloadPdf} className="rounded-full bg-gold text-navy text-sm font-bold px-4 py-2 flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform duration-300"><Download size={15} /> PDF</button>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* left column */}
        <div className="space-y-6">
          <Section title="Financial Health Score">
            <div className="grid place-items-center">
              <ScoreMeter score={lead.score} colorKey={lead.rating?.color} size={170} label={lead.rating?.band} />
              <div className="mt-3 text-sm font-heading font-bold" style={{ color: colorFor(lead.rating?.color) }}>{lead.rating?.label}</div>
            </div>
          </Section>

          <Section title="Follow-up">
            <div className="text-xs text-slate-400 mb-1.5">Status</div>
            <Select value={lead.follow_up_status} onValueChange={updateStatus}>
              <SelectTrigger data-testid="followup-status" className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>{FOLLOWUP_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-xs text-slate-400 mt-4 mb-1.5">Add follow-up date</div>
            <div className="flex gap-2">
              <Input data-testid="followup-date" type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} className="rounded-lg" />
              <button data-testid="followup-add" onClick={setFollowUp} className="rounded-lg bg-navy text-white px-3 shrink-0"><Plus size={16} /></button>
            </div>
            {lead.follow_ups?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {lead.follow_ups.map((f) => (
                  <div key={f.id} className="text-xs text-slate-500">📅 {f.date} <span className="text-slate-300">· {f.by}</span></div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Category Scores">
            <div className="space-y-3">
              {report && Object.values(report.categories).map((c) => (
                <div key={c.code}>
                  <div className="flex justify-between text-xs font-semibold text-slate-600 mb-1">
                    <span>{c.label}</span>
                    <span>{c.applicable ? `${c.percentage}%` : "N/A"}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${c.percentage || 0}%`, background: colorFor(c.color) }} />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* middle + right */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Client Profile">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="City" value={lead.city} />
              <Field label="Age Group" value={lead.age_group} />
              <Field label="Income Range" value={lead.income_range} />
              <Field label="Marital Status" value={q.marital_status} />
              <Field label="Dependents" value={q.dependents} />
              <Field label="Children" value={q.children} />
              <Field label="Monthly Investment" value={q.current_monthly_investment} />
              <Field label="Primary Goal" value={q.primary_goal} />
              <Field label="Consent" value={lead.consent ? "Yes" : "No"} />
              <Field label="Source" value={lead.source} />
              <Field label="Campaign" value={lead.campaign} />
            </div>
            {q.existing_investments?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-1.5">Existing Investments</div>
                <div className="flex flex-wrap gap-2">
                  {q.existing_investments.map((i) => <span key={i} className="text-xs rounded-full bg-royal/5 text-royal px-3 py-1 font-semibold">{i}</span>)}
                </div>
              </div>
            )}
          </Section>

          <Section title="Tags">
            <div className="flex flex-wrap gap-2 mb-3" data-testid="lead-tags">
              {(lead.tags || []).length === 0 && <span className="text-xs text-slate-400">No tags yet.</span>}
              {(lead.tags || []).map((t) => (
                <span key={t} className="text-xs rounded-full bg-navy/5 text-navy px-3 py-1 font-semibold flex items-center gap-1.5">
                  {t}
                  <button data-testid={`tag-remove-${t}`} onClick={() => removeTag(t)} className="text-slate-400 hover:text-branddanger"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="tag-input" value={newTag} onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="e.g. campaign:instagram, vip, referral" className="rounded-lg" />
              <button data-testid="tag-add" onClick={addTag} className="rounded-lg bg-navy text-white px-4 shrink-0 flex items-center"><Plus size={16} /></button>
            </div>
          </Section>

          <Section title="Top 3 Priorities">
            <div className="grid sm:grid-cols-3 gap-4">
              {lead.priorities?.map((p, i) => (
                <div key={p.category} className="rounded-xl border border-slate-100 p-4">
                  <div className="font-heading font-bold text-gold">0{i + 1}</div>
                  <div className="mt-1 font-heading font-semibold text-navy text-sm">{p.title}</div>
                  <div className="mt-1 text-xs text-slate-500 leading-relaxed">{p.message}</div>
                </div>
              ))}
            </div>
          </Section>

          {report?.gaps?.length > 0 && (
            <Section title="Identified Gaps">
              <div className="space-y-3">
                {report.gaps.map((g) => (
                  <div key={g.code} className="flex gap-3">
                    <div className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: g.severity === "high" ? "#D64545" : "#F2A900" }} />
                    <div><span className="font-semibold text-navy text-sm">{g.title}</span><p className="text-xs text-slate-500">{g.suggestion}</p></div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="All 12 Answers">
            <div className="space-y-3">
              {answers_detail.map((a) => (
                <div key={a.code} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
                  <div className="text-xs text-slate-600 max-w-md">
                    <span className="text-[10px] tracking-wide uppercase text-slate-400 font-bold">{a.category}</span>
                    <div className="mt-0.5">{a.question}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-navy">{a.answer_label}</div>
                    <div className="text-xs text-slate-400">{a.points === null ? "N/A" : `${a.points} pts`}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Admin Notes">
            <div className="flex gap-2">
              <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="rounded-lg min-h-[44px]" rows={1} />
              <button data-testid="note-add" onClick={addNote} disabled={savingNote} className="rounded-lg bg-navy text-white px-4 shrink-0 flex items-center">
                {savingNote ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {(lead.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="text-sm text-navy">{n.text}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{n.by} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <div className="text-xs text-slate-400">No notes yet.</div>}
            </div>
          </Section>
        </div>
      </div>
    </AdminShell>
  );
}
